import React from 'react';
import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';

import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { useWelcomeTour } from '../../contexts/WelcomeTourContext';
import { SHOWCASE_META } from '../docs/showcaseData';

export const SHOWCASE_OFFER_TITLE = 'See how people use keepr';
export const SHOWCASE_OFFER_TEXT = SHOWCASE_META.description;

// How long an account counts as new (KPE-062 Q5: "for 30 days").
export const SHOWCASE_OFFER_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

// Accounts an admin made (users.kind, keepr-api utils/accountKinds.js) are not
// people arriving, and the marketing demo account is recorded for videos
// (KPE-038; KPR-1309 resets it nightly, so its age alone would not keep it
// out): none of them is shown the card, so recordings stay clean.
const ADMIN_MADE_KINDS = ['test', 'demo'];
export const DEMO_ACCOUNT_EMAILS = ['demo@keepr.cloud'];

/**
 * Whether this account is one the card is for: created within the last 30
 * days (an account with no readable creation date is not), and neither a test
 * or demo account nor the marketing demo account. `now` is a millisecond time.
 */
export function showcaseCardEligible(user, now = Date.now()) {
	if (!user) return false;
	if (ADMIN_MADE_KINDS.includes(user.kind)) return false;
	if (DEMO_ACCOUNT_EMAILS.includes(String(user.email || '').trim().toLowerCase())) return false;
	const created = Date.parse(user.createdAt);
	if (!Number.isFinite(created)) return false;
	const age = now - created;
	return age >= -DAY_MS && age < SHOWCASE_OFFER_DAYS * DAY_MS;
}

/**
 * The Showcase card on Home (KPR-1930, KPE-062): "See how people use keepr",
 * under the welcome tour's offer, for a new account. Watch opens the Showcase;
 * Not now saves the card as dismissed on the account (WelcomeTourContext,
 * record `showcase-card`), so it does not return on any device. Hidden while
 * the account or the saved answer is unknown, and when keepr could not read
 * the saved answer. The same calm panel as the tour's offer (home.css).
 */
export default function ShowcaseOffer() {
	const { user } = useCurrentUser();
	const { showcaseCard } = useWelcomeTour();
	const navigate = useNavigate();
	if (!showcaseCard || !showcaseCard.offered || !showcaseCardEligible(user)) return null;

	return (
		<section className="welcome-offer" aria-labelledby="showcase-offer-title" data-testid="showcase-offer">
			<div className="welcome-offer__body">
				<h2 className="welcome-offer__title" id="showcase-offer-title">{SHOWCASE_OFFER_TITLE}</h2>
				<p className="welcome-offer__text">{SHOWCASE_OFFER_TEXT}</p>
			</div>
			<div className="welcome-offer__actions">
				<Button onClick={() => navigate(SHOWCASE_META.path)}>Watch</Button>
				<Button type="text" onClick={showcaseCard.dismiss}>Not now</Button>
			</div>
		</section>
	);
}
